import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useLoadScript, Autocomplete } from '@react-google-maps/api'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Loader2 } from 'lucide-react'
import RecurrenceRuleInput from './RecurrenceRuleInput'
import { api } from '../../lib/api'

const libraries = ['places']

const toLocalDatetimeString = (isoString) => {
  if (!isoString) return ''
  const d = new Date(isoString)
  const offset = d.getTimezoneOffset()
  const local = new Date(d.getTime() - offset * 60_000)
  return local.toISOString().slice(0, 16)
}

export default function EventForm({
  initialValues,
  onSubmit,
  loading = false,
  parishId,
  initialGroupId = '',
}) {
  const iv = initialValues || {}

  const [title, setTitle] = useState(iv.title || '')
  const [description, setDescription] = useState(iv.description || '')
  const [startTime, setStartTime] = useState(toLocalDatetimeString(iv.startTime) || '')
  const [endTime, setEndTime] = useState(toLocalDatetimeString(iv.endTime) || '')
  const [locationName, setLocationName] = useState(iv.locationName || '')
  const [locationAddress, setLocationAddress] = useState(iv.locationAddress || '')
  const [latitude, setLatitude] = useState(iv.latitude ?? '')
  const [longitude, setLongitude] = useState(iv.longitude ?? '')
  const [hostId, setHostId] = useState(iv.host?.id || iv.hostId || '')
  const [recurrenceRule, setRecurrenceRule] = useState(iv.recurrenceRule || '')
  const [rsvpVisibility, setRsvpVisibility] = useState(iv.rsvpVisibility || 'private')
  const [isPublished, setIsPublished] = useState(iv.isPublished ?? false)
  const [groupId, setGroupId] = useState(iv.groupId || initialGroupId || '')

  const isCreateMode = !initialValues

  // Parish admins for host selector
  const [admins, setAdmins] = useState([])
  const [adminsLoading, setAdminsLoading] = useState(false)

  // Parish groups for visibility selector (create mode only)
  const [groups, setGroups] = useState([])
  const [groupsLoading, setGroupsLoading] = useState(false)

  // Google Places
  const autocompleteRef = useRef(null)
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  })

  useEffect(() => {
    if (!parishId) return
    setAdminsLoading(true)
    api.getParishAdmins(parishId)
      .then((data) => {
        setAdmins(data || [])
        // Auto-select first admin if no host set
        if (!hostId && data?.length > 0) {
          setHostId(data[0].id)
        }
      })
      .catch(() => setAdmins([]))
      .finally(() => setAdminsLoading(false))
  }, [parishId])

  useEffect(() => {
    if (!parishId) return
    setGroupsLoading(true)
    api.getParishGroups(parishId)
      .then((data) => setGroups(data || []))
      .catch(() => setGroups([]))
      .finally(() => setGroupsLoading(false))
  }, [parishId])

  const onPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace()
    if (!place?.geometry) return

    setLocationAddress(place.formatted_address || '')
    setLatitude(place.geometry.location.lat())
    setLongitude(place.geometry.location.lng())
  }, [])

  const handleRecurrenceChange = useCallback((val) => {
    setRecurrenceRule(val)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const data = {
      title: title.trim(),
      description: description.trim(),
      startTime: new Date(startTime).toISOString(),
      endTime: new Date(endTime).toISOString(),
      locationName: locationName.trim(),
      locationAddress: locationAddress.trim(),
      latitude: parseFloat(latitude) || 0,
      longitude: parseFloat(longitude) || 0,
      hostId,
      rsvpVisibility,
      isPublished,
    }
    if (recurrenceRule) {
      data.recurrenceRule = recurrenceRule
    }
    if (isCreateMode && groupId) {
      data.groupId = groupId
    }
    await onSubmit(data)
  }

  const isValid =
    title.trim() &&
    description.trim() &&
    startTime &&
    endTime &&
    locationName.trim() &&
    locationAddress.trim() &&
    hostId

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div>
        <Label htmlFor="event-title">Title</Label>
        <Input
          id="event-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Event title"
          maxLength={200}
          className="mt-1 bg-surface-linen border-surface-border text-charcoal"
        />
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="event-description">Description</Label>
        <textarea
          id="event-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the event..."
          rows={4}
          className="mt-1 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal placeholder:text-charcoal-muted focus:outline-none focus:ring-2 focus:ring-gold"
        />
      </div>

      {/* Start/End Times */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="event-start">Start Time</Label>
          <Input
            id="event-start"
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="mt-1 bg-surface-linen border-surface-border text-charcoal"
          />
        </div>
        <div>
          <Label htmlFor="event-end">End Time</Label>
          <Input
            id="event-end"
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="mt-1 bg-surface-linen border-surface-border text-charcoal"
          />
        </div>
      </div>

      {/* Location Name */}
      <div>
        <Label htmlFor="event-location-name">Location Name</Label>
        <Input
          id="event-location-name"
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          placeholder="e.g. Parish Hall, Main Church"
          className="mt-1 bg-surface-linen border-surface-border text-charcoal"
        />
      </div>

      {/* Location Address — Google Places Autocomplete */}
      <div>
        <Label htmlFor="event-location-address">Location Address</Label>
        {isLoaded ? (
          <Autocomplete
            onLoad={(ac) => (autocompleteRef.current = ac)}
            onPlaceChanged={onPlaceChanged}
          >
            <Input
              id="event-location-address"
              value={locationAddress}
              onChange={(e) => setLocationAddress(e.target.value)}
              placeholder="Start typing an address..."
              className="mt-1 bg-surface-linen border-surface-border text-charcoal"
            />
          </Autocomplete>
        ) : (
          <Input
            id="event-location-address"
            value={locationAddress}
            onChange={(e) => setLocationAddress(e.target.value)}
            placeholder="Full street address"
            className="mt-1 bg-surface-linen border-surface-border text-charcoal"
          />
        )}
        {latitude && longitude && (
          <p className="mt-1 text-xs text-charcoal-muted">
            Coordinates: {Number(latitude).toFixed(6)}, {Number(longitude).toFixed(6)}
          </p>
        )}
      </div>

      {/* Host — Admin Selector */}
      <div>
        <Label htmlFor="event-host">Host</Label>
        {adminsLoading ? (
          <div className="mt-1 flex items-center gap-2 text-sm text-charcoal-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading admins...
          </div>
        ) : admins.length > 0 ? (
          <select
            id="event-host"
            value={hostId}
            onChange={(e) => setHostId(e.target.value)}
            className="mt-1 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="">Select a host...</option>
            {admins.map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.name} ({admin.email})
              </option>
            ))}
          </select>
        ) : (
          <p className="mt-1 text-sm text-charcoal-muted">No admins found for this parish.</p>
        )}
      </div>

      {/* Visibility — parish-wide or a private group (set at creation only) */}
      <div>
        <Label htmlFor="event-visibility">Visibility</Label>
        {isCreateMode ? (
          groupsLoading ? (
            <div className="mt-1 flex items-center gap-2 text-sm text-charcoal-muted">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading groups...
            </div>
          ) : (
            <>
              <select
                id="event-visibility"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="mt-1 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="">Parish-wide</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-charcoal-muted">
                A group event is private to that group's members and cannot be changed later.
              </p>
            </>
          )
        ) : (
          <p className="mt-1 text-sm text-charcoal-muted">
            {iv.group
              ? `Private to ${iv.group.name}. Visibility cannot be changed after creation.`
              : 'Parish-wide. Visibility cannot be changed after creation.'}
          </p>
        )}
      </div>

      <RecurrenceRuleInput value={recurrenceRule} onChange={handleRecurrenceChange} />

      {/* RSVP Visibility */}
      <div>
        <Label className="mb-2 block">RSVP Visibility</Label>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer">
            <input type="radio" name="rsvpVisibility" value="public" checked={rsvpVisibility === 'public'} onChange={(e) => setRsvpVisibility(e.target.value)} className="text-gold focus:ring-gold" />
            Public
          </label>
          <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer">
            <input type="radio" name="rsvpVisibility" value="private" checked={rsvpVisibility === 'private'} onChange={(e) => setRsvpVisibility(e.target.value)} className="text-gold focus:ring-gold" />
            Private
          </label>
        </div>
      </div>

      {/* Published */}
      <div className="flex items-center gap-2">
        <input type="checkbox" id="event-published" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} className="rounded border-surface-border text-gold focus:ring-gold" />
        <Label htmlFor="event-published" className="text-sm text-charcoal-muted cursor-pointer">Published</Label>
      </div>

      <Button type="submit" disabled={loading || !isValid} className="bg-gold text-black hover:bg-gold/90">
        {loading ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</>) : initialValues ? 'Update Event' : 'Create Event'}
      </Button>
    </form>
  )
}
